/** Journey stepper steps shown on list rows (order matters). */
export const JOURNEY_STEPS = [
  {
    key: 'signed_up',
    label: 'Signed up',
    description: 'Created a Newsfiliate account (standard or MCP signup).',
    activityNames: ['nf_signup', 'mcp_signup'],
  },
  {
    key: 'kit_connected',
    label: 'Kit connected',
    description: 'Connected their Kit account (OAuth or kit_authorized).',
    activityNames: ['kit_authorized'],
    providerType: 'kit',
  },
  {
    key: 'data_ready',
    label: 'Data ready',
    description: 'Newsletter and offer data finished syncing.',
    activityNames: ['data_ready'],
  },
  {
    key: 'extension_widget',
    label: 'Extension / widget',
    description: 'Installed the browser extension or used the product widget.',
    activityNames: ['extension_installed', 'widget_used', 'widget_search'],
  },
  {
    key: 'mcp_used',
    label: 'MCP used',
    description: 'Ran at least one MCP tool successfully (e.g. generate_ideas).',
    mcpSuccessRequired: true,
  },
];

/** e.g. mcp_signup → Mcp Signup, affiliate_link_inserted → Affiliate Link Inserted */
export function formatActivityName(activityName) {
  if (!activityName || typeof activityName !== 'string') return '';
  return activityName
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export function getActivityLabel(activityName) {
  const formatted = formatActivityName(activityName);
  return formatted || activityName;
}

/**
 * Collapse repeated activity_name rows for display. Preserves full timeline separately for raw export.
 * @param {Array<{ activityName: string, label?: string, activityTimestamp: string }>} events
 */
export function groupActivityTimeline(events = []) {
  const groups = new Map();
  for (const event of events) {
    const key = event.activityName;
    if (!key) continue;
    if (!groups.has(key)) {
      groups.set(key, {
        activityName: key,
        label: event.label ?? getActivityLabel(key),
        count: 0,
        firstAt: event.activityTimestamp ?? null,
        lastAt: event.activityTimestamp ?? null,
      });
    }
    const group = groups.get(key);
    group.count += 1;
    const ts = event.activityTimestamp;
    if (ts) {
      const time = new Date(ts).getTime();
      if (!group.lastAt || time > new Date(group.lastAt).getTime()) group.lastAt = ts;
      if (!group.firstAt || time < new Date(group.firstAt).getTime()) group.firstAt = ts;
    }
  }
  return [...groups.values()].sort(
    (a, b) => new Date(b.lastAt ?? 0).getTime() - new Date(a.lastAt ?? 0).getTime()
  );
}

export function buildProviderSources(hasKit, hasKitMcp) {
  const kit = Boolean(hasKit);
  const mcp = Boolean(hasKitMcp);
  const providerTypes = [];
  if (kit) providerTypes.push('kit');
  if (mcp) providerTypes.push('kit_mcp');
  let appMode = 'kit';
  if (mcp) appMode = kit ? 'hybrid' : 'mcp';
  return { providerTypes, appMode };
}

/**
 * @param {object} userRow
 * @param {string[]} milestones - distinct activity names for user
 */
export function computeJourneySteps(userRow, milestones = []) {
  const milestoneSet = new Set(milestones);
  const providerSources = userRow.providerSources ?? buildProviderSources(userRow.hasKit, userRow.hasKitMcp);

  return JOURNEY_STEPS.map((step) => {
    let complete = false;
    if (step.mcpSuccessRequired) {
      complete = Number(userRow.mcpSuccessCount ?? 0) > 0;
    } else if (step.providerType) {
      complete =
        providerSources.providerTypes?.includes(step.providerType) ||
        step.activityNames?.some((n) => milestoneSet.has(n));
    } else if (step.activityNames?.length) {
      complete = step.activityNames.some((n) => milestoneSet.has(n));
    }
    return { key: step.key, label: step.label, description: step.description, complete };
  });
}
